import { Divider, Stack, Typography } from '@mui/material';
import {
  type ScanJob,
  JobStatus,
  type BackupJob,
  type RestoreJob,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { CopyToClipboardButton } from '@/components/buttons/copyToClipboardButton';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { StatusCardWrapper } from '@/components/panels/instance/tabs/overview-tab/statusCardWrapper';
import {
  isBackupJob,
  isRestoreJob,
  isScanJob,
} from '@/components/queryBuilder/properties';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { ContactSupportButton } from '@/components/support/support';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';
import { JobStatuses } from '@/data/inventory/data';
import { formatDuration, isDateValid } from '@/utils/dateTime';
import { dayjs, DATE_FORMAT, TIME_FORMAT } from '@/utils/dayjs';

import { JobStatusIcon } from './jobStatusIcon';

export const JobStatusCard = ({
  job,
}: {
  job: BackupJob | RestoreJob | ScanJob;
}) => {
  return (
    <Stack paddingX='40px' margin={'40px 0'}>
      <StatusCardWrapper
        borderColor={JobStatuses[job.jobExecutionDetails.status].iconColor}
      >
        <Stack gap='32px'>
          <JobStatusHeader job={job} />
          <Divider sx={{ margin: '0 40px', borderStyle: 'dashed' }} />
          <JobStatusFooter job={job} />
        </Stack>
      </StatusCardWrapper>
    </Stack>
  );
};

const JobStatusFooter = ({
  job,
}: {
  job: BackupJob | RestoreJob | ScanJob;
}) => {
  let startText = 'Start time';
  let endText = 'End time';
  let start: Date | null | undefined = job.jobExecutionDetails.startTime;
  let end: Date | null | undefined = job.jobExecutionDetails.endTime;

  if (job.jobExecutionDetails.status === JobStatus.JOB_PENDING) {
    startText = 'Creation time';
    start = job.jobExecutionDetails.createdTime;
    endText = 'Expected start time';
    end = job.jobExecutionDetails.expectedStartTime;
  }
  const accessDeniedItemsCount = 0; // todo: get from server
  return (
    <Stack gap='24px'>
      <JobTimeRangeStatus
        startText={startText}
        endText={endText}
        start={start}
        end={end}
      />
      {(isBackupJob(job) || isRestoreJob(job)) &&
        job.vault?.id &&
        job.snapshotDetails && (
          <SnapshotToolbar
            pointInTime={job.snapshotDetails?.pointInTime}
            vaultId={job.vault?.id}
            accessDeniedItemsCount={accessDeniedItemsCount}
            hideAccessDeniedItemsCount={true}
          />
        )}
    </Stack>
  );
};

const JobTimeRangeStatus = ({
  startText,
  endText,
  start,
  end,
}: {
  startText: string;
  endText: string;
  start?: Date | null;
  end?: Date | null;
}) => {
  return (
    <Stack gap='12px' paddingX='40px'>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography>{startText}</Typography>
        <JobDateStatus date={start} />
      </Stack>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography>{endText}</Typography>
        <JobDateStatus date={end} />
      </Stack>
    </Stack>
  );
};

const JobDateStatus = ({ date }: { date?: Date | null }) => {
  if (!isDateValid(date)) {
    return <Typography>--</Typography>;
  }
  return (
    <Stack direction='row' alignItems='center' gap='12px'>
      <Typography>{dayjs.utc(date).format(DATE_FORMAT)}</Typography>
      <Typography>{dayjs.utc(date).format(TIME_FORMAT)} UTC</Typography>
    </Stack>
  );
};

const JobDuration = ({ job }: { job: BackupJob | RestoreJob | ScanJob }) => {
  const status = job.jobExecutionDetails.status;
  if (job.jobExecutionDetails.status === JobStatus.JOB_PENDING) {
    return (
      isDateValid(job.jobExecutionDetails.expectedStartTime) && (
        <Typography variant='body2'>
          Starts in{' '}
          {formatDuration(
            dayjs
              .utc(job.jobExecutionDetails.expectedStartTime)
              .diff(dayjs.utc(), 'seconds')
          )}
        </Typography>
      )
    );
  }

  if (job.jobExecutionDetails.status === JobStatus.JOB_RUNNING) {
    return (
      isDateValid(job.jobExecutionDetails.startTime) && (
        <Typography variant='body2'>
          Running for{' '}
          {formatDuration(
            dayjs
              .utc()
              .diff(dayjs.utc(job.jobExecutionDetails.startTime), 'seconds')
          )}
        </Typography>
      )
    );
  }

  if (job.jobExecutionDetails.durationSeconds) {
    const label =
      status === JobStatus.JOB_FAILED
        ? 'Failed in'
        : status === JobStatus.JOB_CANCELED
          ? 'Cancelled after'
          : 'Finished in';
    return (
      <Typography variant='body2'>
        {label} {formatDuration(job.jobExecutionDetails.durationSeconds)}
      </Typography>
    );
  }

  return null;
};

const JobStatusHeader = ({
  job,
}: {
  job: BackupJob | RestoreJob | ScanJob;
}) => {
  const { eonAccount } = useUser();
  const accountName = eonAccount?.name || eonAccount?.domain || eonAccount?.id;

  return (
    <Stack
      alignItems='center'
      justifyContent='space-between'
      width='100%'
      gap='12px'
      paddingX='40px'
    >
      <Stack
        width='100%'
        direction='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography variant='h4'>Job status</Typography>
        <Stack direction='row' alignItems='center' gap='12px'>
          <JobDuration job={job} />
          <JobCardMenuItem job={job} />
        </Stack>
      </Stack>
      <JobStatusIcon jobStatus={job.jobExecutionDetails.status} />
      <Typography variant='h3'>
        {isRestoreJob(job)
          ? 'Restore'
          : isScanJob(job)
            ? 'Classification'
            : 'Backup'}{' '}
        {JobStatuses[job.jobExecutionDetails.status].title.toLowerCase()}
      </Typography>
      {job.jobExecutionDetails.statusMessage && (
        <Typography width='540px' textAlign='center' variant='body1'>
          {job.jobExecutionDetails.statusMessage}
        </Typography>
      )}
      {job.jobExecutionDetails.status === JobStatus.JOB_FAILED && (
        <Stack direction='row' alignItems='center' gap='12px'>
          {job.jobExecutionDetails.statusMessage && (
            <CopyToClipboardButton
              tooltip='Copy error message'
              textToCopy={job.jobExecutionDetails.statusMessage}
            />
          )}
          <ContactSupportButton
            subject={`Job Error Encountered (ID: ${job.jobExecutionDetails.jobId})`}
            body={`Hi Eon Support Team,

I'm reaching out regarding a failed backup job in my account${accountName ? ` (${accountName})` : ''}.
Below are the details for your reference:

• Job ID: ${job.jobExecutionDetails.jobId}
• Error Message: ${job.jobExecutionDetails.statusMessage}

Thanks in advance!
`}
          />
        </Stack>
      )}
    </Stack>
  );
};

const JobCardMenuItem = ({
  job,
}: {
  job: BackupJob | RestoreJob | ScanJob;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dal = useDAL();

  if (
    isBackupJob(job) ||
    isScanJob(job) ||
    (job.jobExecutionDetails.status !== JobStatus.JOB_RUNNING &&
      job.jobExecutionDetails.status !== JobStatus.JOB_PENDING)
  ) {
    return null;
  }

  return (
    <>
      <OptionMenu
        data-testid='job-card-menu-item'
        options={[
          {
            text: 'Cancel job',
            menuItemProps: {
              onClick: () => setIsOpen(true),
            },
            id: 'job-card-cancel-job',
          },
        ]}
      />
      <ConfirmationDialog
        isOpen={isOpen}
        title='Cancel job'
        description='Are you sure you want to cancel this job?'
        onCancel={() => setIsOpen(false)}
        onConfirm={async () => {
          await dal.jobs.restore.cancel(job.jobExecutionDetails.jobId);
          setIsOpen(false);
        }}
        buttonIconClass='material-symbols-cancel-outline'
        confirmSnackbarText='Restore job has been cancelled.'
        buttonTitle='Cancel job'
        cancelButtonTitle='Dismiss'
      />
    </>
  );
};
